'use client';

import Image from 'next/image';
import { m } from 'framer-motion';
import { CheckCircle2, ArrowRight } from 'lucide-react';
import styles from './ServiceDetails.module.css';

const servicesDetail = [
    {
        id: 'darat',
        title: 'Cargo Darat (Land Transportation)',
        tagline: 'Armada lengkap untuk distribusi darat yang andal di area Jawa-Bali-Sumatera.',
        description: 'Layanan inland trucking kami didukung oleh berbagai jenis armada mulai dari CDE, CDD, Fuso, hingga Wingbox. Kami melayani pengiriman retail (LTL) maupun full truck load (FTL) dengan rute reguler yang menjangkau seluruh Jawa, Bali, dan Sumatera. Tersedia juga layanan Charter & Cunsul Truck untuk kebutuhan khusus.',
        features: [
            'Armada: CDE, CDD, Fuso, Wingbox',
            'Charter & Cunsul Truck',
            'Jawa-Bali-Sumatera coverage',
            'Layanan Door-to-Door',
        ],
        idealFor: 'FMCG, Bahan Bangunan, Sparepart Otomotif, Pindahan Pabrik.',
        image: '/images/service-darat.png',
        detailPath: '/layanan/kargo-darat'
    },
    {
        id: 'laut',
        title: 'Cargo Laut (Sea Freight)',
        tagline: 'Fokus pada Fast Vessel untuk rute Indonesia Timur dengan berbagai jenis kapal dan kondisi pengiriman.',
        description: 'Kami menyediakan layanan pengiriman FCL (Full Container Load) dan LCL (Less than Container Load) melalui berbagai tipe kapal: Fast Vessel (Trucking-RoRo), PELNI Vessel (Kapal Putih), dan Cargo Container Vessel. Layanan kami mencakup Port-to-Port, Door-to-Port, Port-to-Door, dan Door-to-Door, terutama untuk rute Indonesia Timur.',
        features: [
            'Fast Vessel (Trucking - RoRo)',
            'PELNI Vessel (Kapal Putih)',
            'Cargo Container (LCL & FCL)',
            'Port-to-Port, Door-to-Door, dan variasi lainnya',
        ],
        idealFor: 'Alat Berat, Proyek Konstruksi, Distribusi Consumer Goods Indonesia Timur.',
        image: '/images/service-laut.png',
        detailPath: '/layanan/kargo-laut'
    },
    {
        id: 'udara',
        title: 'Cargo Udara (Air Freight)',
        tagline: 'Solusi Door-to-Door super cepat untuk barang mendesak ke seluruh Indonesia.',
        description: 'Ketika kecepatan adalah segalanya, layanan Cargo Udara kami memastikan barang Anda tiba di tujuan dalam hitungan hari atau bahkan jam. Layanan ini dirancang khusus untuk pengiriman Door-to-Door yang super cepat, menjangkau seluruh tujuan di Indonesia.',
        features: [
            'Door-to-Door super cepat',
            'Pengiriman barang mendesak',
            'Penanganan Barang Khusus',
            'Seluruh tujuan Indonesia',
        ],
        idealFor: 'Dokumen Penting, Obat-obatan, Produk Segar, Layanan E-commerce.',
        image: '/images/service-udara.png',
        detailPath: '/layanan/kargo-udara'
    },
    {
        id: 'warehouse',
        title: 'Manajemen Pergudangan',
        tagline: 'Fasilitas penyimpanan modern dengan sistem keamanan 24/7 dan pelaporan inventaris digital.',
        description: 'Bukan sekadar menyimpan barang, layanan warehousing kami terintegrasi dengan sistem manajemen gudang (WMS) terkini. Kami menyediakan layanan in-bound, out-bound, cross-docking, hingga pick & pack untuk menyederhanakan rantai pasok bisnis Anda.',
        features: [
            'Keamanan CCTV 24/7 & Guard Patrol',
            'Sistem WMS Digital',
            'Flexible Space (Sewa per pallet/meter)',
            'Layanan Value-Added (Labelling, Bundling)',
        ],
        idealFor: 'Distributor, Retailer E-commerce, Industri Manufaktur.',
        image: '/images/service-warehouse.png',
        detailPath: '/layanan/pergudangan'
    },
    {
        id: 'towing',
        title: 'Towing & Gendong Mobil',
        tagline: 'Layanan derek dan pengiriman kendaraan 24 jam yang aman di Solo Raya.',
        description: 'Kami menyediakan solusi transportasi kendaraan yang andal melalui layanan Towing dan Gendong Mobil. Melayani kebutuhan darurat (derek 24 jam) maupun pengiriman unit mobil antar kota dengan keamanan terjamin. Fokus utama kami adalah wilayah Solo/Surakarta dan sekitarnya (Solo Raya).',
        features: [
            'Layanan Derek 24 Jam',
            'Gendong Mobil (Single Towing)',
            'Cakupan Wilayah Solo Raya',
            'Pengiriman Aman & Berasuransi',
        ],
        idealFor: 'Pemilik Kendaraan Pribadi, Showroom Mobil, Perusahaan Rental, Keadaan Darurat.',
        image: '/images/service-towing.png',
        detailPath: '/layanan/towing'
    }
];

export default function ServiceDetails() {
    return (
        <section className={`section ${styles.serviceDetails}`}>
            <div className="container">
                <div className={styles.wrapper}>
                    {servicesDetail.map((service, index) => {
                        const isEven = index % 2 === 0;

                        return (
                            <div key={service.id} id={service.id} className={styles.serviceRow}>
                                <m.div
                                    initial={{ opacity: 0, x: isEven ? -50 : 50 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true, margin: '-100px' }}
                                    transition={{ duration: 0.6, ease: "easeOut" }}
                                    className={`${styles.imageColumn} ${!isEven ? styles.orderLast : ''}`}
                                >
                                    <div className={styles.imageWrapper}>
                                        <Image
                                            src={service.image}
                                            alt={service.title}
                                            fill
                                            sizes="(max-width: 768px) 100vw, 50vw"
                                            className={styles.image}
                                        />
                                        <div className={styles.imageOverlay} />
                                    </div>
                                </m.div>

                                <m.div
                                    initial={{ opacity: 0, x: isEven ? 50 : -50 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true, margin: '-100px' }}
                                    transition={{ duration: 0.6, ease: "easeOut", delay: 0.2 }}
                                    className={styles.contentColumn}
                                >
                                    <div className={styles.contentBox}>
                                        <h2 className={styles.title}>{service.title}</h2>
                                        <h3 className={styles.tagline}>{service.tagline}</h3>
                                        <p className={styles.description}>{service.description}</p>

                                        <div className={styles.featuresSection}>
                                            <h4 className={styles.featuresTitle}>Fitur Layanan:</h4>
                                            <ul className={styles.featureList}>
                                                {service.features.map((feature, idx) => (
                                                    <li key={idx} className={styles.featureItem}>
                                                        <CheckCircle2 size={18} className={styles.checkIcon} />
                                                        <span>{feature}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>

                                        <div className={styles.idealForBox}>
                                            <span className={styles.idealLabel}>Ideal untuk:</span>
                                            <span className={styles.idealText}>{service.idealFor}</span>
                                        </div>

                                        <div className={styles.actionWrap}>
                                            <a href="/kontak" className={styles.btnPrimary}>
                                                Konsultasi Layanan
                                                <ArrowRight size={20} strokeWidth={2.5} />
                                            </a>
                                            <a href={service.detailPath} className={styles.btnSecondary}>
                                                Lihat Detail
                                                <ArrowRight size={20} />
                                            </a>
                                        </div>
                                    </div>
                                </m.div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
